import { formatCurrency } from 'common/FormatUtilities';
import { DateTime } from 'luxon';
import React from 'react';
import compostableTag from 'src/assets/svg/order/compostable.svg';
import { ShefProfile } from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { Chip, ChipColor } from 'src/shared/design-system/Chip';
import { FaMapMarkerAlt } from 'src/shared/design-system/FontAwesome';
import styled from 'styled-components';
import { Colors, Spacing } from 'web-common/src/shared/styles';

const SpecialOfferChipsContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const SpecialOfferTopLeftChip = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
`;

const SpecialOfferTopRightChip = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const ChipMapMarker = styled(FaMapMarkerAlt)`
  margin-right: 6px;
  margin-bottom: 2px;
  color: ${({ color }) => color ?? Colors.RADISH};
`;

type OrderByChipProps = { cutOffDateTime: DateTime };

export const OrderByChip: React.FC<OrderByChipProps> = ({ cutOffDateTime }: OrderByChipProps) => (
  <Chip data-cy='order-by-label' text={`Order by ${cutOffDateTime.toFormat('ha').toLocaleLowerCase()}`} />
);

type PromoCodeChipProps = Pick<NonNullable<ShefProfile['explorePagePromoCode']>, 'value' | 'isPercentage'>;

export const PromoCodeChip: React.FC<PromoCodeChipProps> = ({ value, isPercentage }: PromoCodeChipProps) => {
  const { code } = usePromotionalsHook();
  if (code?.code) {
    // Don't show the chip if the user already has a code applied.
    return <></>;
  }
  const valueString = isPercentage ? `${value}%` : formatCurrency(value, true);
  return <Chip data-cy='promo-code-label' text={`${valueString} Off`} color={ChipColor.YELLOW} />;
};

export const PickupOnlyChip: React.FC = () => (
  <Chip data-cy='pick-up-only-label' icon={<ChipMapMarker />} text='Pick up only' />
);

const CompostableIcon = styled.img`
  width: ${Spacing.SINGLE};
  height: ${Spacing.SINGLE};
  margin-right: 6px;
  margin-top: -3px;
`;

export const EcoChip: React.FC = () => (
  <Chip data-cy='eco-chip' icon={<CompostableIcon src={compostableTag} />} text='ECO packaging' />
);

interface SpecialOfferChipsProps {
  topLeftChip?: React.ReactNode;
  topRightChip?: React.ReactNode;
}

export const SpecialOfferChips: React.FC<SpecialOfferChipsProps> = ({ topLeftChip, topRightChip }) => {
  const hasNoChips = topLeftChip == null && topRightChip == null;

  if (hasNoChips) {
    return null;
  }

  return (
    <SpecialOfferChipsContainer>
      {topLeftChip && <SpecialOfferTopLeftChip>{topLeftChip}</SpecialOfferTopLeftChip>}
      {topRightChip && <SpecialOfferTopRightChip>{topRightChip}</SpecialOfferTopRightChip>}
    </SpecialOfferChipsContainer>
  );
};
